import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import { styled } from "@mui/material/styles";
import { connect } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { FeatureGate } from "../../../../../utils/FeatureGate";
import { animateScroll } from "react-scroll";

import StandardIcon from "../../../../../assets/icons/standard.svg";
import RushIcon from "../../../../../assets/icons/rush.svg";
import StatIcon from "../../../../../assets/icons/stat.svg";
import DetailClose from "../../../../../assets/icons/CloseIcon.svg";
import ConfidentialIcon from "../../../../../assets/icons/vip-case.svg";
import { useReadOnly } from "../../../../../utils/useReadOnly";

import {
  loadSlideboxDirDataList,
  setOpenListDirectory,
  updateSlideBoxSlideDetails,
  updateSlideBoxCaseDetails,
  updateSlideboxLabels,
  updateCaseStatusAction,
  getAllCaseDetailsTabsInfoAction,
  getNotesHistoryAction,
  getFileDownloadUrlAction,
  addNotesAction,
  setReturnToWorklistAction,
} from "../../../actions/slidebox-action";

import { getAllPhysiciansAction } from "../../../../adminDashboard/actions/adminDashboard-action";

import ImageTag from "../ImageTag";

import "react-multi-carousel/lib/styles.css";

import { useNameFormat } from "../../../../adminDashboard/components/DashboardItems/useFormat";

import PatientDetailsTab from "./PatientDetailsTab";
import CaseDetailsTab from "./CaseDetailsTab";
import SlidesTab from "./SlidesTab";
import ActivityTab from "./ActivityTab";

import CreateCaseModal from "../../../../../pages/createCase/CreateCaseModal";

const drawerWidth = "min(45vw, 700px)";

const Root = styled("div")({
  display: "flex",
  width: 0,
  zIndex: 1.5,
});

const SidebarHeading = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: 15,
  height: 65,
  backgroundColor: "#ECE7F8",
  width: "100%",
});

const IconIndicators = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  marginRight: 10,
});

const TitleDetails = styled("div")({
  marginTop: 0,
  marginBottom: 0,
});

const PatientDetailsText = styled("p")({
  color: "#2A1B53",
  fontSize: 14,
  marginBottom: 0,
  lineHeight: "25px",
  fontWeight: 500,
});

const MrnText = styled("p")({
  color: "#2A1B53",
  fontSize: 14,
  marginBottom: 0,
  lineHeight: "25px",
  fontWeight: 500,
});

const HistoricalLink = styled("a")({
  color: "#4E12C3",
  fontWeight: 500,
  marginLeft: "auto",
  marginBottom: "1rem",
  textDecoration: "underline",
  cursor: "pointer",

  "&:hover": {
    color: "#4E12C3",
  },
});

const TitleDetailsRight = styled("div")({
  marginLeft: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-end",
});

const CaseName = styled("p")({
  color: "#2A1B53",
  fontSize: 14,
  marginBottom: 0,
  lineHeight: "25px",
  textTransform: "capitalize",
  fontWeight: 500,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
});

const AccessionNo = styled("p")({
  color: "#2A1B53",
  fontSize: 14,
  marginBottom: 0,
  lineHeight: "25px",
  textTransform: "capitalize",
  fontWeight: 500,
});

const CloseIconWrapper = styled("div")({
  marginBottom: "1rem",
  marginLeft: 10,
  opacity: 0.8,
  cursor: "pointer",

  "&:hover": {
    opacity: 1,
  },
});

const TabContainer = styled("div")({
  display: "flex",
});

const Details = ({
  showDetails,
  setShowDetails,
  selectedDetails = {},
  updateSlideBoxDirDetails,
  loadSlideboxDirDataList,
  setOpenListDirectory,
  slideDirectory,
  selectedDirActivities,
  allSildeinfo,
  getAllTabsInfo,
  allAttachmentsInfo,
  selectedPatientInfo,
  selectedCaseInfo,
  memberDetails,
  setSelectedDir,
  updateCaseDetails,
  licenseTypeFeatures,
  updateLabels,
  tabOption,
  setTabOption,
  openNotesSection,
  setOpenNotesSection,
  openLabelsSection,
  setOpenLabelsSection,
  isOpenedFromGroups,
  isFolderSelected,
  setIsFolderSelected,
  notesHistoryData,
  setSelectedSlideData,
  notInViewer,
  selectedList,
  getFileDownloadUrl,
  getNotesHistory,
  addNotesFn,
  referredNotesData,
  getAllPhysicians,
  orderingPhysicians,
  worklistColumnData,
  showAdvancedSearchModal,
  openAllContainers,
  setReturnToWorklist,
  currentTab,
  selectedListItem,
  proceduresList,
}) => {
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [currentCase, setCurrentCase] = useState("");
  const [openCreateCase, setOpenCreateCase] = useState(false);
  const [patientDetails, setPatientDetails] = useState({});
  const [openCaseTab, setOpenCaseTab] = useState(false);
  const [openSlideTab, setOpenSlideTab] = useState(false);

  const historicalCasesRef = useRef(null);

  const toggleExpansible = (e) => {
    let el = e.currentTarget ? e.currentTarget : (e.target ? e.target : e);
    el.classList.toggle("active");
    let content = el.nextElementSibling;
    if (!content) return;
    if (content.style.display === "block") {
      content.style.display = "none";
    } else {
      content.style.display = "block";
    }
  };

  const priorityOptions = useMemo(
    () => [
      {
        value: "standard",
        img: StandardIcon,
      },
      {
        value: "rush",
        img: RushIcon,
      },
      {
        value: "stat",
        img: StatIcon,
      },
    ],
    []
  );

  const priorityImage = useMemo(() => {
    return priorityOptions.find(
      (item) => item.value === selectedDetails.priority
    )?.img;
  }, [priorityOptions, selectedDetails.priority]);

  const selectedProcedure = useMemo(() => {
    return proceduresList?.find(
      (procedure) =>
        +procedure.procedureId === +selectedDetails.procedureId
    );
  }, [proceduresList, selectedDetails.procedureId]);

  const patientAge = useMemo(() => {
    if (!selectedPatientInfo?.dob) return "-";

    return dayjs().diff(selectedPatientInfo.dob, "year");
  }, [selectedPatientInfo?.dob]);

  const patientGender = useMemo(() => {
    const genderMap = {
      Male: "M",
      Female: "F",
    };

    return genderMap[selectedPatientInfo?.gender] || "-";
  }, [selectedPatientInfo?.gender]);

  const handleTabChange = useCallback(
    (tab) => {
      setTabOption(tab);
    },
    [setTabOption]
  );

  const handleCloseCreateCase = useCallback(() => {
    setOpenCreateCase(false);
  }, []);

  const isReadOnly = useReadOnly();

  useEffect(() => {
    if (isReadOnly && tabOption !== "slides") {
      setTabOption("slides");
    }
  }, [isReadOnly, tabOption, setTabOption]);

  useEffect(() => {
    setPatientDetails({
      firstName: selectedPatientInfo?.first_name,
      middleName: selectedPatientInfo?.middle_name,
      lastName: selectedPatientInfo?.last_name,
      mrnNumber: selectedPatientInfo?.mrn_number,
      dob: selectedPatientInfo?.dob,
      age: patientAge,
      gender: selectedPatientInfo?.gender,
    });
  }, [
    patientAge,
    selectedPatientInfo?.dob,
    selectedPatientInfo?.first_name,
    selectedPatientInfo?.gender,
    selectedPatientInfo?.last_name,
    selectedPatientInfo?.middle_name,
    selectedPatientInfo?.mrn_number,
  ]);

  const scrollToBottom = useCallback(() => {
    animateScroll.scrollToBottom({
      containerId: "details-section",
      duration: 100,
    });
  }, []);

  const setShowDetailsPopup = useCallback(() => {
    setEditing(false);
    setShowDetails(false);
  }, [setShowDetails]);

  const dateSort = useCallback((a, b) => {
    return new Date(b.procedure_date) - new Date(a.procedure_date);
  }, []);

  const handleOpenHistoricalCase = useCallback(
    (caseId) => {
      setSelectedDir({
        dirId: caseId,
        parentDir: 1,
        dirType: "case",
      });

      setCurrentCase(selectedDetails.dirDataId);

      setTabOption("slides");

      getAllTabsInfo({
        dirId: caseId,
        dirType: "case",
      });
    },
    [
      getAllTabsInfo,
      selectedDetails.dirDataId,
      setSelectedDir,
      setTabOption,
    ]
  );

  return (
    <Root>
      <CssBaseline />

      <Drawer
        variant="persistent"
        anchor="right"
        open={showDetails && !showAdvancedSearchModal}
        PaperProps={{
          sx: {
            width: drawerWidth,
            top: notInViewer ? "0px" : "76px",
            position: notInViewer ? "relative" : "fixed",
            zIndex: 1,
            height: notInViewer
              ? "calc(100vh - 120px)"
              : "inherit",
            bottom: !notInViewer ? "20px" : "unset",
            overflow: "hidden",
          },
        }}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: !notInViewer ? "hidden" : "visible",
            height: "100%",
            paddingTop: 0,
          }}
        >
          <SidebarHeading>
            <IconIndicators>
              <img
                src={priorityImage}
                style={{
                  opacity: selectedDetails.priority ? 1 : 0,
                }}
                alt="priority"
              />

              <FeatureGate feature="patient_level_information">
                <div>
                  <ImageTag
                    src={ConfidentialIcon}
                    alt="confidential patient"
                    style={{ opacity: 0 }}
                  />
                </div>
              </FeatureGate>
            </IconIndicators>

            <FeatureGate feature="patient_level_information">
              <TitleDetails>
                <PatientDetailsText>
                  {`${useNameFormat(
                    `${selectedPatientInfo?.first_name || ""} ${selectedPatientInfo?.middle_name || ""
                    }`,
                    selectedPatientInfo?.last_name || ""
                  )} / ${patientGender} / ${patientAge}`}
                </PatientDetailsText>

                <MrnText>
                  {`MRN : ${selectedPatientInfo?.mrn_number || "-"
                    }`}
                </MrnText>
              </TitleDetails>
            </FeatureGate>

            {tabOption === "patient" ? (
              <HistoricalLink
                onClick={() => {
                  if (currentCase) {
                    setSelectedDir({
                      dirId: currentCase,
                      parentDir: 1,
                      dirType: "case",
                    });
                  } else {
                    historicalCasesRef.current?.click();
                  }

                  scrollToBottom();
                }}
              >
                {currentCase
                  ? "New cases available"
                  : "Historical data available"}
              </HistoricalLink>
            ) : (
              <TitleDetailsRight>
                <CaseName>
                  {selectedProcedure?.procedureName || "—"}
                </CaseName>

                <AccessionNo>
                  Acc.No: {selectedDetails.accessionNumber || "—"}
                </AccessionNo>
              </TitleDetailsRight>
            )}

            <CloseIconWrapper>
              <ImageTag
                src={DetailClose}
                alt="close"
                onClick={setShowDetailsPopup}
              />
            </CloseIconWrapper>
          </SidebarHeading>

          <TabContainer
            className="tabOption"
            style={isReadOnly ? { justifyContent: "flex-start" } : {}}
          >
            {!isReadOnly && (
              <FeatureGate feature="patient_level_information">
                <div
                  className={tabOption === "patient" ? "active tab" : "tab"}
                  onClick={() => handleTabChange("patient")}
                >
                  <span>PATIENT</span>
                </div>
              </FeatureGate>
            )}

            {!isReadOnly && (
              <div
                className={tabOption === "case" ? "active tab" : "tab"}
                onClick={() => handleTabChange("case")}
              >
                <span>CASE</span>
              </div>
            )}

            <div
              className={(isReadOnly || tabOption === "slides") ? "active tab" : "tab"}
              onClick={() => handleTabChange("slides")}
            >
              <span>SLIDES</span>
            </div>

            {!isReadOnly && (
              <div
                className={tabOption === "activity" ? "active tab" : "tab"}
                onClick={() => handleTabChange("activity")}
              >
                <span>ACTIVITY</span>
              </div>
            )}
          </TabContainer>


          {!isReadOnly && tabOption === "patient" && (
            <PatientDetailsTab
              selectedDetails={selectedDetails}
              setSelectedDir={setSelectedDir}
              setCurrentCase={setCurrentCase}
              selectedPatientInfo={selectedPatientInfo}
              historicalCasesRef={historicalCasesRef}
              dateSort={dateSort}
              updateSlideBoxDirDetails={updateSlideBoxDirDetails}
              handleOpenHistoricalCase={handleOpenHistoricalCase}
              selectedDirId={selectedList}
              toggleExpansible={toggleExpansible}
            />
          )}

          {!isReadOnly && tabOption === "case" && (
            <CaseDetailsTab
              proceduresList={proceduresList}
              worklistColumnData={worklistColumnData}
              selectedCaseInfo={selectedCaseInfo}
              editing={editing}
              setEditing={setEditing}
              setCurrentCase={setCurrentCase}
              selectedDetails={selectedDetails}
              setSelectedDir={setSelectedDir}
              allAttachmentsInfo={allAttachmentsInfo}
              selectedPatientInfo={selectedPatientInfo}
              dateSort={dateSort}
              handleOpenHistoricalCase={handleOpenHistoricalCase}
              memberDetails={memberDetails}
              updateCaseDetails={updateCaseDetails}
              licenseTypeFeatures={licenseTypeFeatures}
              updateLabels={updateLabels}
              allPatientDetails={patientDetails}
              openCreateCase={openCreateCase}
              setOpenCreateCase={setOpenCreateCase}
              setOpenFromCaseTab={setOpenCaseTab}
              setOpenFromSlideTab={setOpenSlideTab}
              openNotesSection={openNotesSection}
              setOpenNotesSection={setOpenNotesSection}
              openLabelsSection={openLabelsSection}
              setOpenLabelsSection={setOpenLabelsSection}
              getFileDownloadUrl={getFileDownloadUrl}
              selectedDirId={selectedList}
              notesHistoryData={notesHistoryData}
              getNotesHistory={getNotesHistory}
              addNotesFn={addNotesFn}
              referredNotesData={referredNotesData}
              getAllPhysicians={getAllPhysicians}
              orderingPhysicians={orderingPhysicians}
              toggleExpansible={toggleExpansible}
            />
          )}

          {tabOption === "slides" && (
            <SlidesTab
              selectedListItem={selectedListItem}
              currentTab={currentTab}
              setReturnToWorklist={setReturnToWorklist}
              openAllContainers={openAllContainers}
              navigate={navigate}
              selectedDetails={selectedDetails}
              setSelectedDir={setSelectedDir}
              setCurrentCase={setCurrentCase}
              allSildeinfo={allSildeinfo}
              setOpenCreateCase={setOpenCreateCase}
              setOpenFromSlideTab={setOpenSlideTab}
              setOpenFromCaseTab={setOpenCaseTab}
              openCreateCase={openCreateCase}
              selectedPatientInfo={selectedPatientInfo}
              dateSort={dateSort}
              handleOpenHistoricalCase={handleOpenHistoricalCase}
              isOpenedFromGroups={isOpenedFromGroups}
              isFolderSelected={isFolderSelected}
              setIsFolderSelected={setIsFolderSelected}
              setSelectedSlideData={setSelectedSlideData}
              licenseTypeFeatures={licenseTypeFeatures}
              toggleExpansible={toggleExpansible}
            />
          )}

          {!isReadOnly && tabOption === "activity" && (
            <ActivityTab activityData={selectedDirActivities} />
          )}
        </List>

        <Tooltip
          id="slide-name"
          place="bottom"
          variant="dark"
        />
      </Drawer>

      {openCreateCase && (
        <CreateCaseModal
          onClose={handleCloseCreateCase}
          licenseTypeFeatures={licenseTypeFeatures}
          openSlideTab={openSlideTab}
          allPatientDetails={patientDetails}
          openCaseTab={openCaseTab}
          existingCaseId={selectedDetails.dirDataId}
        />
      )}
    </Root>
  );
};

const mapStateToProps = (state) => ({
  slideDirectory: state.SlideBox.folders,
  selectedDirActivities: state.SlideBox.selectedDirActivities,
  userDetails: state.Global.loggedUserDetails,
  selectedCaseAssignUsers: state.SlideBox.selectedCaseAssignUsers,
  ...state.SlideBox.selectedFolderData,
  ...state.SlideBox.selectedListData,
  allSildeinfo: state.SlideBox.allSlides,
  allAttachmentsInfo: state.SlideBox.allAttachments,
  memberDetails: state.SlideBox.userList,
  slideDirDataList: state.SlideBox.dirDataList,
  selectedPatientInfo: state.SlideBox.selectedPatientInfo,
  selectedCaseInfo: state.SlideBox.selectedCaseInfo,
  licenseTypeFeatures:
    state.Global.loggedUserDetails.licenseTypeAccessData,
  notesHistoryData: state.SlideBox.notesHistoryData,
  referredNotesData: state.SlideBox.referredNotesData,
  orderingPhysicians: state.Dashboard.orderingPhysicians,
  currentTab: state.SlideBox.currentTab,
  proceduresList: state.Dashboard.proceduresList,
});

const mapDispatchToProps = (dispatch) => ({
  updateSlideBoxDirDetails: (payload) =>
    dispatch(updateSlideBoxSlideDetails(payload)),

  updateCaseDetails: (payload) =>
    dispatch(updateSlideBoxCaseDetails(payload)),

  updateLabels: (payload) =>
    dispatch(updateSlideboxLabels(payload)),

  loadSlideboxDirDataList: (payload) =>
    dispatch(loadSlideboxDirDataList(payload)),

  setOpenListDirectory: (payload) =>
    dispatch(setOpenListDirectory(payload)),

  updateCaseStatus: (payload) =>
    dispatch(updateCaseStatusAction(payload)),

  getAllTabsInfo: (payload) =>
    dispatch(getAllCaseDetailsTabsInfoAction(payload)),

  getNotesHistory: (payload) =>
    dispatch(getNotesHistoryAction(payload)),

  getFileDownloadUrl: (payload, callbackFn) =>
    dispatch(getFileDownloadUrlAction(payload, callbackFn)),

  addNotesFn: (payload) =>
    dispatch(addNotesAction(payload)),

  getAllPhysicians: () =>
    dispatch(getAllPhysiciansAction()),

  setReturnToWorklist: (payload) =>
    dispatch(setReturnToWorklistAction(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Details);